import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Plus, FileText, Download, Printer, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BoldTextarea } from "@/components/BoldTextarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ProposalEditor = ({ prop, onChange }) => {
  const secs = prop.sections || [];
  const setSecs = (v) => onChange({ ...prop, sections: v });
  const upd = (i, p) => setSecs(secs.map((s, j) => (j === i ? { ...s, ...p } : s)));
  const move = (i, d) => { const a = [...secs]; [a[i], a[i + d]] = [a[i + d], a[i]]; setSecs(a); };
  return (
    <div className="space-y-4" data-testid="maint-proposal-editor">
      <div className="card-premium grid gap-3 p-6 md:grid-cols-2">
        {[["title", "Judul"], ["subtitle", "Sub Judul (Gedung/Site)"], ["valid_text", "Periode Kontrak"], ["place_date", "Tempat & Tanggal"]].map(([k, l]) => (
          <label key={k} className="text-xs font-semibold uppercase tracking-wide text-slate-500">{l}
            <Input className="mt-1 bg-white normal-case" value={prop[k] || ""} onChange={(e) => onChange({ ...prop, [k]: e.target.value })} data-testid={`maint-prop-${k}`} />
          </label>
        ))}
        <p className="md:col-span-2 text-[11px] text-slate-500">Format isi: baris diawali <b>- </b> jadi bullet, <b>1. </b> jadi nomor, <b>**teks**</b> tebal. Sisipkan tabel otomatis dengan baris <code>[[PERANGKAT]]</code>, <code>[[BACKUP]]</code>, <code>[[HARGA]]</code>, <code>[[BACKUP_TEXT]]</code>; teks <code>{"{visits}"}</code>, <code>{"{period}"}</code>, <code>{"{term}"}</code> otomatis mengikuti opsi.</p>
      </div>
      {secs.map((s, i) => (
        <div key={i} className="card-premium p-5" data-testid={`maint-sec-${i}`}>
          <div className="mb-2 flex items-center gap-2">
            <span className="font-heading text-lg font-bold text-blue-800">{i + 1}.</span>
            <Input className="bg-white font-semibold" value={s.title} onChange={(e) => upd(i, { title: e.target.value })} data-testid={`maint-sec-title-${i}`} />
            <Button size="icon" variant="ghost" disabled={!i} onClick={() => move(i, -1)} data-testid={`maint-sec-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" disabled={i === secs.length - 1} onClick={() => move(i, 1)} data-testid={`maint-sec-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => window.confirm(`Hapus bagian "${s.title}"?`) && setSecs(secs.filter((_, j) => j !== i))} data-testid={`maint-sec-del-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
          </div>
          <BoldTextarea rows={Math.min(Math.max((s.body || "").split("\n").length + 1, 3), 18)} className="text-sm" value={s.body} onChange={(v) => upd(i, { body: v })} testId={`maint-sec-body-${i}`} />
        </div>
      ))}
      <Button variant="outline" className="bg-white" onClick={() => setSecs([...secs, { title: "Bagian Baru", body: "" }])} data-testid="maint-sec-add-btn"><Plus className="h-4 w-4 mr-1" />Tambah Bagian</Button>
    </div>
  );
};

export const MaintPdfPreview = ({ m, open, onOpenChange, kind = "proposal" }) => {
  const cost = kind === "cost";
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!open || !m?.id) return undefined;
    let u = "";
    setUrl("");
    api.get(`/maintenance/${m.id}/${cost ? "cost-pdf" : "pdf"}`, { responseType: "blob" })
      .then((r) => { u = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); setUrl(u); })
      .catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
    return () => u && URL.revokeObjectURL(u);
  }, [open, m?.id, m?.updated_at, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = cost ? `KALKULASI-MAINTENANCE_${m?.ms_no || ""}.pdf` : `PROPOSAL-MAINTENANCE_${(m?.customer_name || "").replace(/[^A-Za-z0-9]+/g, "-")}_${m?.selected?.backup ? "BACKUP" : "NON-BACKUP"}_${m?.ms_no || ""}.pdf`;
  const dl = () => { const a = document.createElement("a"); a.href = url; a.download = name; a.click(); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="flex h-[92vh] w-[95vw] max-w-5xl flex-col">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><FileText className="h-5 w-5 text-blue-800" />{cost ? `Preview Kalkulasi Cost ${m?.ms_no || ""}` : `Preview Proposal ${m?.ms_no} — ${m?.selected?.backup ? "Backup" : "Non-Backup"}, ${m?.selected?.period} bulan sekali`}</DialogTitle></DialogHeader>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="bg-white" disabled={!url} onClick={() => { const w = window.open(url, "_blank"); w?.addEventListener("load", () => w.print()); }} data-testid="maint-pdf-print-btn"><Printer className="h-4 w-4 mr-1" />Print</Button>
          <Button disabled={!url} onClick={dl} data-testid="maint-pdf-download-btn"><Download className="h-4 w-4 mr-1" />Download</Button>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
          {url ? <iframe title="preview" src={url} className="h-full w-full" data-testid="maint-pdf-frame" /> : <div className="flex h-full items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
